import type { GccEligibilityRule, GccSourcingTier, GccTaskTag } from './types'

// Zod-free so the UI can import it without pulling in the schema layer.

/**
 * GCC eligibility rules (WI 2366). Shared by the GCC pipeline (Step 4 population
 * split) and the Review Task Register page, so the eligibility a reviewer sees is
 * the same split the workforce sizing used.
 */

/**
 * Default eligibility rules, used when a design was saved without any.
 *
 * A task is GCC-eligible when ALL of the following hold:
 *   - Its disposition is Automate, Agentify, or Augment (not fully Human)
 *   - Its sourcing tier is not HQ (proximity-dependent work stays at corporate)
 */
export const DEFAULT_ELIGIBILITY_RULES: GccEligibilityRule[] = [
  {
    field: 'disposition',
    operator: 'in',
    value: ['Automate', 'Agentify', 'Augment'],
  },
  {
    field: 'sourcingTier',
    operator: 'not-equals',
    value: 'HQ',
  },
]

/** Rule fields the engine can evaluate — the ones present on every Step 1 task tag. */
export const SUPPORTED_ELIGIBILITY_FIELDS: ReadonlyArray<GccEligibilityRule['field']> = ['disposition', 'sourcingTier']

/** Eligibility shown on Review Task Register. Unknown = the rules can't be evaluated for this task. */
export type GccTaskEligibility = 'Eligible' | 'Retained' | 'Unknown'

/** Saved rules, or the defaults for designs saved before rules were stored. */
export function resolveEligibilityRules(rules: GccEligibilityRule[] | null | undefined): GccEligibilityRule[] {
  return rules ?? DEFAULT_ELIGIBILITY_RULES
}

/**
 * Throws when any rule uses a field the engine can't evaluate, so generation fails
 * before the first AI call rather than at Step 4.
 */
export function assertSupportedEligibilityRules(rules: GccEligibilityRule[]): void {
  const unsupported = [...new Set(rules.map((r) => r.field).filter((f) => !SUPPORTED_ELIGIBILITY_FIELDS.includes(f)))]
  if (unsupported.length > 0) {
    throw new Error(
      `Eligibility rule field ${unsupported.map((f) => `'${f}'`).join(', ')} is not supported. ` +
        `Supported fields: ${SUPPORTED_ELIGIBILITY_FIELDS.join(', ')}.`
    )
  }
}

/**
 * Returns true when a task's disposition and sourcing tier satisfy the given
 * eligibility rules. All rules must match (AND logic); an empty rule list passes.
 */
export function evaluateEligibilityRules(
  role: { disposition: string; sourcingTier?: GccSourcingTier },
  rules: GccEligibilityRule[]
): boolean {
  for (const rule of rules) {
    if (!(rule.field in role)) {
      throw new Error(
        `Eligibility rule field '${rule.field}' is not available on task tags — implement it before configuring rules that use it.`
      )
    }
    const actual = role[rule.field as keyof typeof role] as string
    switch (rule.operator) {
      case 'equals':
        if (actual !== rule.value) return false
        break
      case 'not-equals':
        if (actual === rule.value) return false
        break
      case 'in':
        if (!Array.isArray(rule.value) || !rule.value.includes(actual)) return false
        break
      case 'not-in':
        if (!Array.isArray(rule.value) || rule.value.includes(actual)) return false
        break
    }
  }
  return true
}

/** Task-name key used to match Step 0 register rows to Step 1 tags. */
export function normalizeTaskName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase()
}

/** Indexes Step 1 task tags by normalized task name. The first tag wins on duplicates. */
export function indexTaskTagsByName(tags: GccTaskTag[] | null | undefined): Map<string, GccTaskTag> {
  const map = new Map<string, GccTaskTag>()
  for (const tag of tags ?? []) {
    const key = normalizeTaskName(tag.taskName)
    if (!map.has(key)) map.set(key, tag)
  }
  return map
}

/** Disposition of one task-register row, as the eligibility rules see it. */
export interface GccRowDisposition {
  /**
   * The Step 1 tag's disposition, or the v1 Step 0 bucket when the task has no tag.
   * Undefined when neither exists: v2 registers carry no bucket (WI 2323).
   */
  disposition?: string
  /** The v1 Step 0 bucket, set only when Step 1 changed it */
  overriddenBucket?: string
  /** The Step 1 sourcing tier; undefined when the task has no tag */
  sourcingTier?: GccSourcingTier
}

/**
 * The disposition and tier the rules are evaluated against for one register row,
 * so the Bucket column shows the same value the eligibility check used (WI 2366).
 */
export function resolveRowDisposition(
  task: { taskName: string; automationBucket?: string },
  tagsByName: Map<string, GccTaskTag>
): GccRowDisposition {
  const tag = tagsByName.get(normalizeTaskName(task.taskName))
  if (!tag) return { disposition: task.automationBucket }
  return {
    disposition: tag.disposition,
    sourcingTier: tag.sourcingTier,
    ...(task.automationBucket !== undefined && tag.disposition !== task.automationBucket && {
      overriddenBucket: task.automationBucket,
    }),
  }
}

/** True when the disposition passes every disposition rule; tier rules are ignored. An unknown disposition never passes. */
export function dispositionMeetsRules(disposition: string | undefined, rules: GccEligibilityRule[]): boolean {
  if (disposition === undefined) return false
  return evaluateEligibilityRules({ disposition }, rules.filter((r) => r.field === 'disposition'))
}

/**
 * Eligibility of one task-register row under the given rules.
 *
 * Uses the matching Step 1 tag (disposition + sourcing tier). Without a tag the
 * v1 Step 0 bucket stands in for disposition, but the sourcing tier is unknown, so
 * any rule that needs it makes the row Unknown. A v2 row without a tag has no
 * disposition at all, so it is Unknown too (WI 2323). Unsupported rule fields also
 * give Unknown rather than throwing, so a bad saved rule can't break the page.
 */
export function resolveTaskEligibility(
  task: { taskName: string; automationBucket?: string },
  tagsByName: Map<string, GccTaskTag>,
  rules: GccEligibilityRule[]
): GccTaskEligibility {
  if (rules.some((r) => !SUPPORTED_ELIGIBILITY_FIELDS.includes(r.field))) return 'Unknown'
  const tag = tagsByName.get(normalizeTaskName(task.taskName))
  if (tag) {
    return evaluateEligibilityRules({ disposition: tag.disposition, sourcingTier: tag.sourcingTier }, rules)
      ? 'Eligible'
      : 'Retained'
  }
  if (rules.some((r) => r.field !== 'disposition')) return 'Unknown'
  if (task.automationBucket === undefined) return rules.length === 0 ? 'Eligible' : 'Unknown'
  return evaluateEligibilityRules({ disposition: task.automationBucket }, rules) ? 'Eligible' : 'Retained'
}

export const NO_DISPOSITION_SELECTED_MESSAGE =
  'Select at least one disposition under "Include tasks with disposition" to generate a GCC design.'

/**
 * True when the rules include at least one disposition. With no included
 * disposition, no task can be GCC-eligible, so generation must not start.
 * Excluded sourcing tiers may be empty, which means no tier is excluded.
 */
export function hasIncludedDisposition(rules: ReadonlyArray<GccEligibilityRule>): boolean {
  return rules.some((r) => {
    if (r.field !== 'disposition') return false
    if (r.operator === 'in') return Array.isArray(r.value) && r.value.length > 0
    if (r.operator === 'equals') return typeof r.value === 'string' && r.value.length > 0
    return false
  })
}
